import API_URLS from "@/constants/apiUrls";
import { HTTP_METHODS } from "@/constants/httpMethods";
import { request } from "@/lib/api";

/**
 * Hotel Landing Service
 * Otel landing page API'leri için servis
 */
export const hotelLandingService = {
  /**
   * Landing page için otel listesini getir
   * @param {Object} params - Parametreler
   * @param {string} params.slug - Landing page slug'ı (örn: "antalya")
   * @param {number} [params.page] - Sayfa numarası (default: 1)
   * @param {Object} [params.userFilterInput] - Kullanıcı filtreleri (opsiyonel)
   * @param {number[]} [params.userFilterInput.stars] - Yıldız filtreleri
   * @param {number} [params.userFilterInput.order] - Sıralama (default: 0)
   * @param {string} [params.userFilterInput.rating] - Rating filtresi
   * @param {number[]} [params.userFilterInput.locations] - Lokasyon ID'leri
   * @param {number[]} [params.userFilterInput.themes] - Tema ID'leri
   * @param {number[]} [params.userFilterInput.amenities] - Tesis özellik ID'leri
   * @param {string} params.languageCode - Dil kodu (örn: "tr", "en") - uppercase olmalı
   * @param {Object} headers - HTTP headers (session-id ve session-token burada olmalı)
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   * 
   * Usage Example:
   * getHotelsList({
   *   slug: "antalya",
   *   page: 1,
   *   userFilterInput: {
   *     stars: [4, 5],
   *     locations: [1, 2],
   *     rating: "8+",
   *     order: 0,
   *     themes: [],
   *     amenities: []
   *   },
   *   languageCode: "TR"
   * }, headers)
   */
  getHotelsList: (
    {
      slug,
      page = 1,
      userFilterInput = {},
      languageCode = "TR",
    } = {},
    headers = {},
    hasErrorHandler = false,
  ) => {
    // UserFilterInput için default değerler
    const defaultUserFilterInput = {
      Stars: [],
      Chains: [],
      Order: 0,
      Rating: "",
      Locations: [],
      Themes: [],
      Amenities: [],
    };

    // UserFilterInput mapping: camelCase -> PascalCase (eğer verilmişse)
    const mappedUserFilterInput = {
      Stars: userFilterInput.stars || defaultUserFilterInput.Stars,
      Chains: userFilterInput.chains || defaultUserFilterInput.Chains,
      Order: userFilterInput.order ?? defaultUserFilterInput.Order,
      Rating: userFilterInput.rating || defaultUserFilterInput.Rating,
      Locations: userFilterInput.locations || defaultUserFilterInput.Locations,
      Themes: userFilterInput.themes || defaultUserFilterInput.Themes,
      Amenities: userFilterInput.amenities || defaultUserFilterInput.Amenities,
    };

    // Payload oluştur (LanguageCode zaten uppercase olarak gelmeli, ama güvenlik için uppercase yapıyoruz)
    const payload = {
      Slug: slug,
      Page: page,
      UserFilterInput: mappedUserFilterInput,
      LanguageCode: languageCode ? languageCode.toUpperCase() : "TR",
    };

    return request({
      method: HTTP_METHODS.POST,
      url: API_URLS.hotel.landing.getHotelsList,
      payload,
      headers,
      hasErrorHandler,
    });
  },

  /**
   * Landing page için içerik ve filtre bilgilerini getir
   * @param {Object} params - Parametreler
   * @param {string} params.slug - Landing page slug'ı (örn: "antalya")
   * @param {string} params.languageCode - Dil kodu (örn: "tr", "en") - lowercase
   * @param {Object} headers - HTTP headers (session-id ve session-token burada olmalı)
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   * 
   * Usage Example:
   * getContentBySlug({
   *   slug: "antalya",
   *   languageCode: "tr"
   * }, headers)
   */
  getContentBySlug: (
    {
      slug,
      languageCode = "tr",
    } = {},
    headers = {},
    hasErrorHandler = false,
  ) => {
    // Payload oluştur
    const payload = {
      Slug: slug,
      LanguageCode: languageCode.toLowerCase(),
    };

    return request({
      method: HTTP_METHODS.POST,
      url: API_URLS.hotel.landing.getContentBySlug,
      payload,
      headers,
      hasErrorHandler,
    });
  },

  /**
   * Landing page için diğer ürünleri getir (uçak, otobüs, araç kiralama vb.)
   * @param {Object} params - Parametreler
   * @param {string} params.slug - Landing page slug'ı (örn: "antalya")
   * @param {string} params.languageCode - Dil kodu (örn: "tr", "en") - lowercase
   * @param {Object} headers - HTTP headers (session-id ve session-token burada olmalı)
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   * 
   * Usage Example:
   * getOtherProducts({
   *   slug: "antalya",
   *   languageCode: "tr"
   * }, headers)
   */
  getOtherProducts: (
    {
      slug,
      languageCode = "tr",
    } = {},
    headers = {},
    hasErrorHandler = false,
  ) => {
    // Payload oluştur
    const payload = {
      Slug: slug,
      LanguageCode: languageCode.toLowerCase(),
    };

    return request({
      method: HTTP_METHODS.POST,
      url: API_URLS.hotel.landing.getOtherProducts,
      payload,
      headers,
      hasErrorHandler,
    });
  },

  /**
   * Landing page için ana içerik metnini getir
   * @param {Object} params - Parametreler
   * @param {string} params.languageCode - Dil kodu (örn: "tr", "en") - lowercase
   * @param {Object} headers - HTTP headers (session-id ve session-token burada olmalı)
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   * 
   * Usage Example:
   * getMainText({
   *   languageCode: "tr"
   * }, headers)
   */
  getMainText: (
    {
      slug,
      languageCode = "tr",
    } = {},
    headers = {},
    hasErrorHandler = false,
  ) => {
    // Payload oluştur
    const payload = {
      Slug: slug,
      LanguageCode: languageCode.toLowerCase(),
    };

    return request({
      method: HTTP_METHODS.POST,
      url: API_URLS.hotel.basic.getMainText,
      payload,
      headers,
      hasErrorHandler,
    });
  },
};


